import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { CardSelect } from '@common/ui/CardSelect';
import { AutoComplete } from '@common/ui/inputs/AutoComplete';
import { Input } from '@common/ui/inputs/Input';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import {
  CANDIDATE_AGE_MAX,
  CANDIDATE_AGE_MIN,
  defaultCandidateAges,
  FRIEND_AGE_MAX,
  FRIEND_AGE_MIN,
  useMatchmakerFriendStore,
  useMatchmakerModeStore,
} from '@entities/matchmaker';
import { cities } from '@entities/profile/constants/cities';

import { analytics } from '@services/AnalyticsService';
import { type FriendSex } from '@services/MatchmakerService';

import styles from './FriendSetup.module.scss';

export type FriendSetupProps = {
  /** Shown inside DatingHome: saving shows the deck in place. */
  isEmbedded?: boolean;
};

const FRIEND_SEX_ITEMS = [
  { value: '0', label: 'Парню' },
  { value: '1', label: 'Девушке' },
];

const DIGITS = /^[0-9]*$/;

const cityItems = cities.map((city) => ({ value: city, title: city }));

const toFriendSex = (value: string | number): FriendSex =>
  String(value) === '1' ? '1' : '0';

const ageError = (age: number | null, min: number, max: number) => {
  if (age === null) return undefined;

  if (age < min) return `Минимальный возраст ${min}`;

  if (age > max) return `Максимальный возраст ${max}`;

  return undefined;
};

const parseAge = (text: string) => (text === '' ? null : Number(text));

/** «Кого сосватаем?» (board 3.2): who the matchmaker is looking for. */
export const FriendSetup = ({ isEmbedded = false }: FriendSetupProps) => {
  const navigate = useNavigate();
  const tabbar = useTabbar();
  // The slot's setter, without making it an effect dependency.
  const tabbarRef = useRef(tabbar);
  tabbarRef.current = tabbar;
  const savedFriend = useMatchmakerFriendStore((state) => state.friend);
  const setFriend = useMatchmakerFriendStore((state) => state.setFriend);
  const setMode = useMatchmakerModeStore((state) => state.setMode);
  const ownCity = useAuthStore((state) => state.user?.city);

  const [sex, setSex] = useState<FriendSex | null>(savedFriend?.sex ?? null);
  const [ageText, setAgeText] = useState(
    savedFriend ? String(savedFriend.age) : '',
  );
  const [city, setCity] = useState(() => {
    if (savedFriend?.city) return savedFriend.city;

    return ownCity && cities.includes(ownCity) ? ownCity : '';
  });
  // null: the server's own range for the friend, shown as the default.
  const [fromText, setFromText] = useState<string | null>(
    savedFriend?.ageFrom != null ? String(savedFriend.ageFrom) : null,
  );
  const [toText, setToText] = useState<string | null>(
    savedFriend?.ageTo != null ? String(savedFriend.ageTo) : null,
  );

  const age = parseAge(ageText);
  const friendAgeError = ageError(age, FRIEND_AGE_MIN, FRIEND_AGE_MAX);
  const isAgeValid = age !== null && !friendAgeError;
  const defaults =
    sex !== null && isAgeValid ? defaultCandidateAges(sex, age) : null;

  const customFrom = fromText === null ? null : parseAge(fromText);
  const customTo = toText === null ? null : parseAge(toText);
  const effectiveFrom = customFrom ?? defaults?.from ?? null;
  const effectiveTo = customTo ?? defaults?.to ?? null;

  const fromError =
    ageError(customFrom, CANDIDATE_AGE_MIN, CANDIDATE_AGE_MAX) ??
    (effectiveFrom !== null &&
    effectiveTo !== null &&
    effectiveFrom > effectiveTo
      ? '«От» больше «До»'
      : undefined);
  const toError = ageError(customTo, CANDIDATE_AGE_MIN, CANDIDATE_AGE_MAX);
  const isRangeDefault = fromText === null && toText === null;

  const isValid =
    sex !== null && isAgeValid && city !== '' && !fromError && !toError;

  const handleSubmit = () => {
    if (sex === null || age === null || !isValid) return;

    setFriend({ sex, age, city, ageFrom: customFrom, ageTo: customTo });
    setMode('matchmaker');
    analytics.trackMmFriendSet();

    if (!isEmbedded) navigate({ to: '/dating' });
  };

  useEffect(() => {
    tabbarRef.current.setBottomSheet(
      <BottomSheet
        buttons={[
          <Button
            testId="friend-setup-submit-button"
            variant="actionPrimary"
            disabled={!isValid}
            onClick={handleSubmit}
          >
            Показать анкеты
          </Button>,
        ]}
      />,
    );
  }, [isValid, sex, age, city, customFrom, customTo]);

  useEffect(() => () => tabbarRef.current.setBottomSheet(undefined), []);

  return (
    <div className={styles.friendSetup} data-test-id="friend-setup">
      <h1 className={styles.title}>Кого сосватаем?</h1>
      <p className={styles.text}>
        Расскажите о друге — покажем анкеты, которые ему подойдут. Друг ничего
        не заполняет.
      </p>

      <p className={styles.label}>Кому ищем пару</p>
      <CardSelect
        items={FRIEND_SEX_ITEMS}
        value={sex ?? ''}
        onChange={(item) => setSex(toFriendSex(item.value))}
      />

      <Input
        data-test-id="friend-setup-age-input"
        placeholder="Возраст друга"
        inputMode="numeric"
        regexp={DIGITS}
        value={ageText}
        error={friendAgeError}
        onChange={(event) => setAgeText(event.target.value)}
      />

      <AutoComplete
        items={cityItems}
        placeholder="Город"
        searchPlaceholder="Поиск города"
        value={city}
        onSelect={setCity}
      />

      <p className={styles.label}>Возраст анкет</p>
      <div>
        <div className={styles.range}>
          <Input
            data-test-id="friend-setup-age-from-input"
            placeholder="От"
            inputMode="numeric"
            regexp={DIGITS}
            value={fromText ?? (defaults ? String(defaults.from) : '')}
            error={fromError}
            onChange={(event) => setFromText(event.target.value)}
          />
          <Input
            data-test-id="friend-setup-age-to-input"
            placeholder="До"
            inputMode="numeric"
            regexp={DIGITS}
            value={toText ?? (defaults ? String(defaults.to) : '')}
            error={toError}
            onChange={(event) => setToText(event.target.value)}
          />
        </div>
        {isRangeDefault && defaults && (
          <div className={styles.hint} data-test-id="friend-setup-range-hint">
            Подобрали по возрасту друга — можно изменить
          </div>
        )}
      </div>

      <p className={styles.footnote}>Сохраним на этом телефоне</p>
    </div>
  );
};
